import { Box, Chip, Stack, Typography } from "@mui/material";
import {
  CHIP_BACKGROUND_COLOR,
  CHIP_TEXT_COLOR,
  MUTED_TEXT_COLOR,
  VALUE_TEXT_COLOR,
  WASTE_CLASSIFICATION_LABELS,
} from "encamp-shared/src/waste/hazardousWasteLabelVisuals";
import { LabelContentFieldsFragment, UnitType } from "generated-graphql/graphql";
import { ReactElement, ReactNode } from "react";

import { containerCapacityAndTypeDescription, formatLabelDate } from "../containerDisplay";
import { SmTextMetrics } from "./labelPreviewMetrics";

/** A `fontSize` sx fragment mirroring `smTextMetrics.valueSize` -- `undefined`
 * (a no-op MUI/emotion drop) when there's no metrics to mirror. Shared by
 * every value `Typography` in `LeftColumn` below, including nested spans
 * that don't inherit their parent's `sx`. */
function valueSx(smTextMetrics: SmTextMetrics | undefined): { fontSize?: string } {
  return { fontSize: smTextMetrics ? `${smTextMetrics.valueSize}px` : undefined };
}

export function FieldGroup({
  label,
  metrics,
  children,
}: {
  label: string;
  metrics?: SmTextMetrics;
  children: ReactNode;
}): ReactElement {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Typography
        variant="overline"
        sx={{
          color: MUTED_TEXT_COLOR,
          lineHeight: 1.4,
          display: "block",
          fontSize: metrics ? `${metrics.fieldLabelSize}px` : undefined,
          letterSpacing: metrics ? `${metrics.fieldLabelLetterSpacing}px` : undefined,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** The CONTENTS field: waste stream name + its classification chip. Split
 * out of `LeftColumn` (which stays too long to also carry this row's own
 * layout) rather than combined there. */
function ContentsField({
  content,
  smTextMetrics,
}: {
  content: LabelContentFieldsFragment;
  smTextMetrics?: SmTextMetrics;
}): ReactElement | null {
  if (!content.wasteStreamName) {
    return null;
  }
  return (
    <FieldGroup label="Contents" metrics={smTextMetrics}>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* flexGrow/flexShrink/minWidth: 0 so a long waste stream name wraps
            within the row instead of pushing the chip past it, and the chip
            (flexShrink: 0) lands flush against the row's right edge --
            mirroring the PDF's LeftColumn (hazardousWasteLabelComponents.tsx),
            which flexGrows the name for the same reason. */}
        <Typography
          sx={{
            fontWeight: 700,
            color: VALUE_TEXT_COLOR,
            flexGrow: 1,
            flexShrink: 1,
            minWidth: 0,
            ...valueSx(smTextMetrics),
          }}
        >
          {content.wasteStreamName}
        </Typography>
        {content.classification ? (
          <Chip
            // The shared, print-matching regulatory wording ("RCRA
            // hazardous"), NOT the Overview charts' friendlier legend copy --
            // this chip must read exactly what the printed label's chip does.
            label={WASTE_CLASSIFICATION_LABELS[content.classification]}
            size="small"
            sx={{
              flexShrink: 0,
              backgroundColor: CHIP_BACKGROUND_COLOR,
              color: CHIP_TEXT_COLOR,
              "& .MuiChip-label": {
                fontSize: smTextMetrics ? `${smTextMetrics.chipTextSize}px` : undefined,
              },
            }}
          />
        ) : null}
      </Stack>
    </FieldGroup>
  );
}

/** The CONTAINER field: the monospace container code, plus an optional
 * " · <capacity/type>" clause. Split out of `LeftColumn` for the same reason
 * as `ContentsField` above -- and because this is the one role QA round 3
 * caught missing its value-size mirror (the nested monospace span doesn't
 * inherit its parent Typography's `sx`). */
function ContainerField({
  content,
  containerType,
  capacity,
  capacityUnit,
  smTextMetrics,
}: {
  content: LabelContentFieldsFragment;
  containerType?: string | null;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
  smTextMetrics?: SmTextMetrics;
}): ReactElement {
  const containerDescription = containerCapacityAndTypeDescription({
    containerType,
    capacity,
    capacityUnit,
  });
  const sizedValueSx = valueSx(smTextMetrics);
  return (
    <FieldGroup label="Container" metrics={smTextMetrics}>
      {containerDescription ? (
        <Typography sx={{ color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
          <Typography component="span" sx={{ fontFamily: "monospace", ...sizedValueSx }}>
            {content.containerLabel}
          </Typography>
          {` · ${containerDescription}`}
        </Typography>
      ) : (
        <Typography sx={{ fontFamily: "monospace", color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
          {content.containerLabel}
        </Typography>
      )}
    </FieldGroup>
  );
}

export function LeftColumn({
  content,
  containerType,
  capacity,
  capacityUnit,
  smTextMetrics,
}: {
  content: LabelContentFieldsFragment;
  containerType?: string | null;
  capacity?: number | null;
  capacityUnit?: UnitType | null;
  smTextMetrics?: SmTextMetrics;
}): ReactElement {
  const hasWasteCodes = content.federalWasteCodes.length > 0;
  const hasStateCodes = content.stateWasteCodes.length > 0;
  const sizedValueSx = valueSx(smTextMetrics);
  return (
    <Stack sx={{ flex: 1 }}>
      <FieldGroup label="Generator" metrics={smTextMetrics}>
        <Typography sx={{ fontWeight: 700, color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
          {content.generatorName}
        </Typography>
        <Typography sx={{ color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
          {content.generatorAddress}
        </Typography>
      </FieldGroup>
      {content.epaId ? (
        <FieldGroup label="EPA ID Number" metrics={smTextMetrics}>
          <Typography sx={{ fontFamily: "monospace", color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
            {content.epaId}
          </Typography>
        </FieldGroup>
      ) : null}
      {content.accumulationStartDate ? (
        <FieldGroup label="Accumulation Start Date" metrics={smTextMetrics}>
          <Typography sx={{ fontWeight: 700, color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
            {formatLabelDate(content.accumulationStartDate)}
          </Typography>
        </FieldGroup>
      ) : null}
      {content.areaName ? (
        <FieldGroup label="Area" metrics={smTextMetrics}>
          <Typography sx={{ fontWeight: 700, color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
            {content.areaName}
          </Typography>
        </FieldGroup>
      ) : null}
      <ContentsField content={content} smTextMetrics={smTextMetrics} />
      {hasWasteCodes ? (
        <FieldGroup label="EPA Waste Codes" metrics={smTextMetrics}>
          <Typography sx={{ fontFamily: "monospace", color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
            {content.federalWasteCodes.join(", ")}
          </Typography>
        </FieldGroup>
      ) : null}
      {hasStateCodes ? (
        <FieldGroup label="State Waste Codes" metrics={smTextMetrics}>
          <Typography sx={{ fontFamily: "monospace", color: VALUE_TEXT_COLOR, ...sizedValueSx }}>
            {content.stateWasteCodes.join(", ")}
          </Typography>
        </FieldGroup>
      ) : null}
      <ContainerField
        content={content}
        containerType={containerType}
        capacity={capacity}
        capacityUnit={capacityUnit}
        smTextMetrics={smTextMetrics}
      />
    </Stack>
  );
}
